import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import toast from "react-hot-toast";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import ListSummaryCards, {
  countWhere,
} from "../../../Components/List/ListSummaryCards";
import ClientSelectAutocomplete from "./ClientSelectAutocomplete";
import {
  assignApplicationToOrganization,
  fetchClientApplicationContext,
  fetchClientsForSelect,
} from "../../../Apis/ClientApplications";

const OWNED_APP_COLUMNS = [
  {
    field: "application_name",
    label: "Application",
    visible: true,
    width: 220,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "status_label",
    label: "Status",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "license_count",
    label: "Licenses",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

export default function AssignOrganizationApplication() {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [contextError, setContextError] = useState(null);
  const [organizationId, setOrganizationId] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setClientsLoading(true);
      try {
        const list = await fetchClientsForSelect();
        if (!cancelled) {
          setClients(
            (list || []).map((c) => ({
              client_id: c.client_id,
              client_name: c.client_name || `Client #${c.client_id}`,
            }))
          );
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.error || err.message || "Failed to load clients."
          );
        }
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadContext = useCallback(async (clientId) => {
    if (!clientId) {
      setContext(null);
      return;
    }
    setContextLoading(true);
    setContextError(null);
    try {
      setContext(await fetchClientApplicationContext(clientId));
    } catch (err) {
      setContext(null);
      setContextError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load client application context."
      );
    } finally {
      setContextLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedClient?.client_id) {
      setContext(null);
      setOrganizationId("");
      setApplicationId("");
      return;
    }
    loadContext(selectedClient.client_id);
    setOrganizationId("");
    setApplicationId("");
  }, [selectedClient, loadContext]);

  const selectedOrganization = useMemo(() => {
    if (!context?.organizations?.length || !organizationId) return null;
    return context.organizations.find(
      (o) => String(o.organization_id) === String(organizationId)
    );
  }, [context, organizationId]);

  const clientOwnedApps = useMemo(() => {
    return (context?.assigned_applications || []).filter((a) => a.is_active);
  }, [context]);

  const assignableToOrg = useMemo(() => {
    if (!clientOwnedApps.length) return [];
    const linkedIds = new Set(
      (selectedOrganization?.linked_application_ids || []).map(Number)
    );
    return clientOwnedApps.map((app) => ({
      ...app,
      alreadyOnOrg: linkedIds.has(Number(app.application_id)),
    }));
  }, [clientOwnedApps, selectedOrganization]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient?.client_id) {
      toast.error("Select a client.");
      return;
    }
    if (!organizationId) {
      toast.error("Select an organization.");
      return;
    }
    if (!applicationId) {
      toast.error("Select an application.");
      return;
    }

    const meta = assignableToOrg.find(
      (a) => String(a.application_id) === String(applicationId)
    );
    if (meta?.alreadyOnOrg) {
      toast.error("This application is already on the selected organization.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await assignApplicationToOrganization(
        selectedClient.client_id,
        {
          organization_id: Number(organizationId),
          application_id: Number(applicationId),
        }
      );

      if (result.already_assigned) {
        toast.success(result.message || "Already assigned.");
      } else {
        const admin = result.admin_assignment;
        const adminNote =
          admin?.assigned && admin?.organization_user_id
            ? ` Admin assigned to org user #${admin.organization_user_id}.`
            : admin?.message
              ? ` ${admin.message}`
              : "";
        toast.success(
          `${result.message || "Done."} Roles: ${result.roles_seeded ?? 0}, permissions: ${result.permissions_seeded ?? 0}.${adminNote}`
        );
      }

      await loadContext(selectedClient.client_id);
      setApplicationId("");
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to assign application to organization."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const ownedAppRows = useMemo(
    () =>
      (clientOwnedApps || []).map((app) => ({
        ...app,
        status_label: app.is_active ? "Active" : "Inactive",
      })),
    [clientOwnedApps]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "owned",
        label: "Client apps",
        value: ownedAppRows.length,
        iconKey: "apps",
      },
      {
        key: "orgs",
        label: "Organizations",
        value: (context?.organizations || []).length,
        iconKey: "building",
      },
      {
        key: "active",
        label: "Active apps",
        value: countWhere(ownedAppRows, (row) => Boolean(row.is_active)),
        iconKey: "check",
        tone: "ok",
      },
    ],
    [ownedAppRows, context]
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Layout4
        heading="Assign Application to Organization"
        subtitle="Pick a client-owned application, choose an organization, then copy roles and permissions into that org."
        showAddButton={false}
      />
      <ListSummaryCards cards={summaryCards} />

      <Paper sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <AppRegistrationIcon color="primary" />
          <Typography variant="h6">Organization access and roles</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Pick a client-owned application, choose an organization, then copy
          roles and permissions into that org. The app must already be assigned
          to the client first.
        </Typography>

        {clientsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <ClientSelectAutocomplete
              clients={clients}
              selectedClient={selectedClient}
              onChange={(_, value) => setSelectedClient(value)}
            />

            {contextLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {contextError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {contextError}
              </Alert>
            )}

            {context && !contextLoading && (
              <>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Applications on client (eligible for org assign)
                  </Typography>
                  {clientOwnedApps.length === 0 ? (
                    <Alert severity="warning">
                      No applications on this client. Assign the application to
                      the client first.
                    </Alert>
                  ) : (
                    <TableDataGeneric
                      tableName="assigned_organization_applications"
                      primaryKey="application_id"
                      heading="Client applications"
                      data={ownedAppRows}
                      sortname="application_name"
                      showActions={false}
                      hideToolbar={true}
                      token={localStorage.getItem("token")}
                      configss={OWNED_APP_COLUMNS}
                    />
                  )}
                </Box>

                <FormControl fullWidth margin="normal" required>
                  <InputLabel id="org-select-label">Organization</InputLabel>
                  <Select
                    labelId="org-select-label"
                    label="Organization"
                    value={organizationId}
                    onChange={(e) => {
                      setOrganizationId(e.target.value);
                      setApplicationId("");
                    }}
                  >
                    <MenuItem value="">
                      <em>Select organization</em>
                    </MenuItem>
                    {(context.organizations || []).map((org) => (
                      <MenuItem key={org.organization_id} value={org.organization_id}>
                        {org.organization_name}
                        {org.organization_short_name
                          ? ` (${org.organization_short_name})`
                          : ""}
                        {(org.linked_applications || []).length > 0
                          ? ` — has: ${org.linked_applications
                              .map((a) => a.application_name)
                              .join(", ")}`
                          : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedOrganization && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Apps on this organization:{" "}
                    {(selectedOrganization.linked_applications || []).length
                      ? selectedOrganization.linked_applications
                          .map((a) => a.application_name)
                          .join(", ")
                      : "none"}
                  </Typography>
                )}

                <FormControl
                  fullWidth
                  margin="normal"
                  required
                  disabled={!organizationId || clientOwnedApps.length === 0}
                >
                  <InputLabel id="org-app-label">Application to enable on org</InputLabel>
                  <Select
                    labelId="org-app-label"
                    label="Application to enable on org"
                    value={applicationId}
                    onChange={(e) => setApplicationId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select application</em>
                    </MenuItem>
                    {assignableToOrg.map((app) => (
                      <MenuItem
                        key={app.application_id}
                        value={app.application_id}
                        disabled={app.alreadyOnOrg}
                      >
                        {app.application_name}
                        {app.alreadyOnOrg ? " (already on this org)" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      submitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    disabled={
                      submitting ||
                      !organizationId ||
                      !applicationId ||
                      clientOwnedApps.length === 0
                    }
                  >
                    Assign to organization &amp; copy roles
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
